import { Link, useLocation } from "wouter";
import { authClient, useSignOut } from "@/lib/auth-client";
import { AccountDialog } from "@/components/AccountDialog";
import { useEffect, useState } from "react";
import { LayoutDashboard, Mail, FileCode2, Settings, History, LogOut, Loader2, Users, IdCard, Inbox as InboxIcon, ShieldCheck, Trash2, ScrollText, Search as SearchIcon, Webhook, Puzzle, KeyRound, MapPin, ShieldAlert, BarChart3 } from "lucide-react";
import {
  useGetMe,
  getGetMeQueryKey,
  useListInboundMailboxes,
  getListInboundMailboxesQueryKey,
} from "@workspace/api-client-react";
import { ApiError } from "@workspace/api-client-react";
import { capitalizeName } from "@/lib/capitalizeName";
import { Badge } from "@/components/ui/badge";
import { NotAuthorized } from "@/components/NotAuthorized";

export function AppShell({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { data: session } = authClient.useSession();
  const user = session?.user;
  const signOut = useSignOut();
  const [accountOpen, setAccountOpen] = useState(false);

  // Global "/" shortcut — pressing the slash key anywhere outside of a
  // text input jumps the user to the Search page. Mirrors Gmail / GitHub
  // / Linear / etc, where "/" is the universally-known "search" hotkey.
  // We intentionally do NOT preventDefault inside text inputs so users
  // can still type forward-slashes in the compose body, the search box
  // itself, and any other editable surface.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "/") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      if (location !== "/app/search") {
        setLocation("/app/search");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location, setLocation]);

  // Ensure user is created in DB. We disable react-query retries here
  // because a 403 from the email-domain allowlist is a definitive
  // "no" — retrying would just delay the NotAuthorized screen and
  // generate noisy backend traffic.
  const { data: me, isLoading: meLoading, error: meError } = useGetMe({
    query: {
      queryKey: getGetMeQueryKey(),
      retry: false,
      refetchOnWindowFocus: false,
    },
  });
  const isAdmin = me?.isAdmin === true;

  // Inbox unread badge — refresh every 60s so newly delivered mail
  // shows up without forcing a full page reload. Only admins can
  // access the inbox, so skip the fetch entirely for non-admins.
  const mailboxesQuery = useListInboundMailboxes(undefined, {
    query: {
      queryKey: getListInboundMailboxesQueryKey(),
      refetchInterval: 60_000,
      enabled: isAdmin,
    },
  });
  const totalUnread =
    mailboxesQuery.data?.mailboxes.reduce(
      (acc, m) => acc + (m.unreadCount ?? 0),
      0,
    ) ?? 0;

  // The /me endpoint returns 403 with body { error: "email_domain_not_allowed",
  // message, allowedDomains } when a user signs in with an email outside
  // the workspace's allowlist. Show a dedicated screen instead of the
  // app shell so they can sign back out cleanly.
  const domainBlocked =
    meError instanceof ApiError &&
    meError.status === 403 &&
    (meError.data as { error?: string } | null)?.error === "email_domain_not_allowed";
  if (domainBlocked) {
    const data = (meError as ApiError<{ message?: string; allowedDomains?: string[] }>).data;
    return (
      <NotAuthorized
        message={data?.message ?? null}
        allowedDomains={data?.allowedDomains ?? []}
      />
    );
  }

  const navigation = [
    { name: "Dashboard", href: "/app", icon: LayoutDashboard, badge: 0 },
    { name: "Search", href: "/app/search", icon: SearchIcon, badge: 0 },
    { name: "Compose", href: "/app/compose", icon: Mail, badge: 0 },
    ...(isAdmin
      ? [{ name: "Inbox", href: "/app/inbox", icon: InboxIcon, badge: totalUnread }]
      : []),
    { name: "Templates", href: "/app/templates", icon: FileCode2, badge: 0 },
    { name: "Snippets", href: "/app/snippets", icon: Puzzle, badge: 0 },
    { name: "Contacts", href: "/app/contacts", icon: Users, badge: 0 },
    { name: "Logs", href: "/app/logs", icon: History, badge: 0 },
    { name: "Engagement", href: "/app/engagement", icon: MapPin, badge: 0 },
    { name: "Representative", href: "/app/representative", icon: IdCard, badge: 0 },
    { name: "Brand", href: "/app/brand", icon: Settings, badge: 0 },
    { name: "Webhooks", href: "/app/webhooks", icon: Webhook, badge: 0 },
    { name: "API Keys", href: "/app/api-keys", icon: KeyRound, badge: 0 },
    ...(isAdmin
      ? [
          { name: "Users", href: "/app/admin/users", icon: ShieldCheck, badge: 0 },
          { name: "Trashed Templates", href: "/app/admin/templates-trash", icon: Trash2, badge: 0 },
          { name: "Audit Log", href: "/app/admin/audit-log", icon: ScrollText, badge: 0 },
          { name: "Rate-Limit Alerts", href: "/app/admin/rate-limit-alerts", icon: ShieldAlert, badge: 0 },
          { name: "Email Analytics", href: "/app/admin/email-analytics", icon: BarChart3, badge: 0 },
        ]
      : []),
  ];

  const displayName = capitalizeName(user?.name) || user?.email || "User";
  const avatarChar =
    (user?.name && capitalizeName(user.name).charAt(0)) ||
    user?.email?.charAt(0)?.toUpperCase() ||
    "U";

  if (meLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar — sticky so the user profile + Sign out always stay
          visible without needing to scroll the page. h-screen pins it to
          the viewport; the nav section scrolls internally if it ever
          overflows so the bottom block (avatar + Sign out) stays anchored. */}
      <div className="w-64 border-r border-border bg-sidebar flex flex-col sticky top-0 h-screen self-start">
        <div className="h-16 flex items-center px-6 border-b border-border shrink-0">
          <Link href="/app" className="flex items-center gap-2 font-bold text-xl text-sidebar-foreground">
            <Mail className="h-6 w-6 text-primary" />
            <span>MailCraft</span>
          </Link>
        </div>
        
        <div className="flex-1 py-6 flex flex-col gap-1 px-4 overflow-y-auto min-h-0">
          {navigation.map((item) => {
            const isActive = location === item.href || (item.href !== "/app" && location.startsWith(item.href));
            return (
              <Link 
                key={item.name} 
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${
                  isActive 
                    ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                }`}
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.name}</span>
                {item.badge > 0 && (
                  <Badge
                    variant={isActive ? "secondary" : "default"}
                    className="ml-auto h-5 min-w-5 px-1.5 text-[10px]"
                    data-testid={`nav-badge-${item.name.toLowerCase()}`}
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>

        <div className="p-4 border-t border-border">
          <button
            type="button"
            onClick={() => setAccountOpen(true)}
            className="w-full text-left flex items-center gap-3 px-3 py-2 mb-2 rounded-md transition-colors hover:bg-sidebar-accent/50"
            data-testid="button-open-account"
            title="Account settings"
          >
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
              {avatarChar}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-medium text-foreground truncate"
                data-testid="text-user-display-name"
              >
                {displayName}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
            </div>
          </button>
          <AccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
          <button
            onClick={() => signOut()}
            data-testid="button-sign-out"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm font-medium text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {children}
      </main>
    </div>
  );
}
